import { type QueryRef, useBackgroundQuery, useReadQuery, useSuspenseQuery } from '@apollo/client/react';

import { AppsDocument, type IAppsQuery, type IAppsQueryVariables } from 'src/graphql/actions/apps/apps.query';
import { ProfileDocument } from 'src/graphql/actions/profile/profile.query';
import { ApplicationsCard } from 'src/containers/Home/Applications';
import { AssuranceCard } from 'src/containers/Home/Assurance';
import { FinishProtectingCard } from 'src/containers/Home/Protections';
import { ProtectionByApplicationCard } from 'src/containers/Home/ProtectionByApplication';
import { UnreadTasksContainer } from 'src/containers/Home/Tasks';
import { Main } from 'src/components/Layout';
import { withSuspense } from 'src/hoc';

import { ComingSoonCard, UnclaimedBanner, WelcomeEmptyState } from './ui';

const getGreeting = (date: Date) => {
  const hour = date.getHours();

  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 18) return 'Good day';

  return 'Good evening';
};

type IAppsQueryRef = QueryRef<IAppsQuery, IAppsQueryVariables>;

type IHomeContentProps = {
  appsQueryRef: IAppsQueryRef;
  unclaimedQueryRef: IAppsQueryRef;
};

const HomeContent = ({ appsQueryRef, unclaimedQueryRef }: IHomeContentProps) => {
  const {
    data: { profile },
  } = useSuspenseQuery(ProfileDocument);
  const {
    data: { apps },
  } = useReadQuery(appsQueryRef);

  const firstName = profile.name?.split(' ')[0];

  if (!apps.nodesCount) return <WelcomeEmptyState firstName={firstName} />;

  return (
    <div className="container mx-auto space-y-6">
      <div className="space-y-2">
        <h1 className="text-xl text-foreground font-extrabold">
          {getGreeting(new Date())}
          {firstName && `, ${firstName}`}
        </h1>
        <p className="text-sm text-muted-foreground">Here&apos;s where your software resilience stands today.</p>
      </div>
      <UnclaimedBanner unclaimedQueryRef={unclaimedQueryRef} />
      <UnreadTasksContainer />
      <div className="grid gap-4 md:grid-cols-3">
        <ApplicationsCard unclaimedQueryRef={unclaimedQueryRef} />
        <FinishProtectingCard />
        <ComingSoonCard />
      </div>
      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <ProtectionByApplicationCard className="xl:col-span-2" />
        <AssuranceCard />
      </div>
    </div>
  );
};

const SuspendedHomeContent = withSuspense(HomeContent);

const HomePage = () => {
  const [appsQueryRef] = useBackgroundQuery(AppsDocument, {
    fetchPolicy: 'cache-and-network',
    variables: { perPage: 0 },
  });
  const [unclaimedQueryRef] = useBackgroundQuery(AppsDocument, {
    fetchPolicy: 'cache-and-network',
    variables: { unclaimed: true, perPage: 0 },
  });

  return (
    <Main title="Home">
      <SuspendedHomeContent appsQueryRef={appsQueryRef} unclaimedQueryRef={unclaimedQueryRef} />
    </Main>
  );
};

export default HomePage;
